import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  getDepositFiatCurrencyInputOptions,
  useDebounce,
  useDepositData,
} from '@/shared/lib';
import {
  useCountriesDictionary,
  useCreateDepositOperation,
  useDepositCommission,
} from '@/shared/api';
import { COMMISSION_CHECK_DELAY } from '@/shared/config';
import { type DepositFormValues } from '@/shared/types';

// Extended form values for bank transfer
export interface BankTransferFormValues extends DepositFormValues {
  bankCountryCode: string;
  bankKey: string;
}

interface UseDepositBankTransferFormLogicOptions {
  onSubmit?: (data: BankTransferFormValues) => void;
  onSuccess?: (invoiceId: string) => void;
  onError?: (error: Error) => void;
  initialData?: BankTransferFormValues | null;
}

export const useDepositBankTransferFormLogic = ({
  onSubmit,
  onSuccess,
  onError,
  initialData,
}: UseDepositBankTransferFormLogicOptions = {}) => {
  const { t } = useTranslation();
  const [, setFormData] = useState<BankTransferFormValues | null>(
    initialData || null
  );

  const {
    accountNumber,
    depositWithdrawAssetsData,
    assetsIconsData,
    banksData,
  } = useDepositData();

  const { countriesData, isCountriesLoading, countriesError } =
    useCountriesDictionary();

  // State management for dynamic validation (like shared form logic)
  const [currentMinAmount, setCurrentMinAmount] = useState(0);
  const [currentCurrencyCode, setCurrentCurrencyCode] = useState('');

  // Create form with current minAmount and currencyCode for validation
  const bankTransferForm = useForm<BankTransferFormValues>({
    mode: 'onChange',
    resolver: zodResolver(
      z.object({
        bankCountryCode: z
          .string()
          .nonempty({ message: t('authPages.validation.requiredField') }),
        currencyCode: z
          .string()
          .nonempty({ message: t('authPages.validation.requiredField') }),
        bankKey: z
          .string()
          .nonempty({ message: t('authPages.validation.requiredField') }),
        amount: z
          .string({ error: t('authPages.validation.requiredField') })
          .refine(
            val => (currentMinAmount ? Number(val) >= currentMinAmount : true),
            {
              message: currentMinAmount
                ? t('depositMethods.bankTransfer.validation.minAmount', {
                    amount: currentMinAmount,
                    currency: currentCurrencyCode || '',
                  })
                : t('authPages.validation.requiredField'),
            }
          ),
      })
    ),
    defaultValues: {
      bankCountryCode: initialData?.bankCountryCode || '',
      currencyCode: initialData?.currencyCode || '',
      bankKey: initialData?.bankKey || '',
      amount: String(initialData?.amount),
    },
  });

  const watchedValues = bankTransferForm.watch();

  // Currency options based on selected country (use actual form values)
  const currencyOptions = useMemo(() => {
    return getDepositFiatCurrencyInputOptions(
      'bank',
      depositWithdrawAssetsData?.value,
      assetsIconsData?.value,
      banksData?.value,
      watchedValues.bankCountryCode
    );
  }, [
    depositWithdrawAssetsData,
    assetsIconsData,
    banksData,
    watchedValues.bankCountryCode,
  ]);

  // Calculate minAmount based on bank transfer specific logic (use actual form values)
  const calculatedMinAmount = useMemo(() => {
    if (
      !watchedValues.bankCountryCode ||
      !watchedValues.currencyCode ||
      !watchedValues.bankKey
    ) {
      return 0;
    }

    const selectedOption = currencyOptions.find(
      o => o.value === watchedValues.currencyCode
    );

    return selectedOption?.min || 0;
  }, [
    watchedValues.bankCountryCode,
    watchedValues.currencyCode,
    watchedValues.bankKey,
    currencyOptions,
  ]);

  // Update state when minAmount or currencyCode changes
  useEffect(() => {
    setCurrentMinAmount(calculatedMinAmount);
    setCurrentCurrencyCode(watchedValues.currencyCode);
  }, [calculatedMinAmount, watchedValues.currencyCode]);

  const queryClient = useQueryClient();

  const {
    mutate: createDeposit,
    isCreateOperationDepositLoading,
    createOperationDepositError,
  } = useCreateDepositOperation({
    operationType: 'bank',
    onSuccess: (data: { invoiceId: string }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-history'] });
      onSuccess?.(data.invoiceId);
    },
    onError: error => {
      onError?.(error);
    },
  });

  // Commission logic (using shared logic for basic functionality)
  const debouncedAmount = useDebounce(
    watchedValues.amount,
    COMMISSION_CHECK_DELAY
  );

  const normalizedAmount = watchedValues.amount ?? NaN;

  const hasCommissionInputs = Boolean(
    watchedValues.bankCountryCode &&
      watchedValues.currencyCode &&
      watchedValues.bankKey &&
      !Number.isNaN(normalizedAmount) &&
      Number(normalizedAmount) > 0
  );

  const isCommissionEnabled = !!(
    watchedValues.bankCountryCode &&
    watchedValues.currencyCode &&
    watchedValues.bankKey &&
    !Number.isNaN(debouncedAmount) &&
    Number(debouncedAmount) > 0
  );

  const {
    data: commissionData,
    isLoading: isCommissionFetching,
    error: commissionError,
  } = useDepositCommission({
    operationType: 'bank',
    params: {
      bankCountryCode: watchedValues.bankCountryCode,
      currencyCode: watchedValues.currencyCode,
      amount: debouncedAmount,
      bankKey: watchedValues.bankKey,
    },
    enabled: isCommissionEnabled,
  });

  const filteredCountries = useMemo(() => {
    if (!countriesData?.value || !banksData?.value?.deposit?.countries)
      return [];

    const bankCountryCodes = banksData.value.deposit.countries.map(
      bc => bc.countryCode
    );

    return countriesData.value.filter(country =>
      bankCountryCodes.includes(country.code)
    );
  }, [countriesData, banksData]);

  const bankOptions = useMemo(() => {
    if (
      !banksData?.value?.deposit?.countries ||
      !watchedValues.bankCountryCode ||
      !watchedValues.currencyCode
    )
      return [];

    const selectedCountry = banksData.value.deposit.countries.find(
      c => c.countryCode === watchedValues.bankCountryCode
    );
    const selectedCurrency = selectedCountry?.currencies?.find(
      cur => cur.code === watchedValues.currencyCode
    );

    const bankKeys = selectedCurrency?.banks || [];
    return (
      selectedCountry?.banks
        ?.filter(bank => bankKeys.includes(bank.bankKey))
        .map(bank => ({
          value: bank.bankKey,
          label: bank.bankName,
        })) || []
    );
  }, [banksData, watchedValues.bankCountryCode, watchedValues.currencyCode]);

  useEffect(() => {
    bankTransferForm.setValue('currencyCode', '');
    bankTransferForm.setValue('bankKey', '');
  }, [watchedValues.bankCountryCode, bankTransferForm]);

  useEffect(() => {
    bankTransferForm.setValue('bankKey', '');
  }, [watchedValues.currencyCode, bankTransferForm]);

  const isAmountDebouncing =
    hasCommissionInputs && watchedValues.amount !== debouncedAmount;
  const isCommissionPending =
    hasCommissionInputs && (isAmountDebouncing || isCommissionFetching);
  const isCommissionData = commissionData && typeof commissionData === 'object';
  const shouldShowCommission =
    !Object.keys(bankTransferForm.formState.errors).length &&
    hasCommissionInputs &&
    !isCommissionPending &&
    isCommissionData &&
    !commissionError;

  const isSubmitInProgressWithoutErrors =
    (isCreateOperationDepositLoading || isCommissionPending) &&
    !(
      commissionError ||
      Object.keys(bankTransferForm.formState.errors).length > 0
    );

  const handleFormSubmit = (data: BankTransferFormValues) => {
    if (
      Object.keys(bankTransferForm.formState.errors).length > 0 ||
      Boolean(commissionError) ||
      isSubmitInProgressWithoutErrors ||
      !isCommissionData
    ) {
      return;
    }

    setFormData(data);
    createDeposit({ ...data, accountNumber });
    onSubmit?.(data);
  };

  // Use current minAmount and currencyCode for display
  const minAmount = currentMinAmount;
  const amountPlaceholder = currentCurrencyCode
    ? t('common.amountMinAmountPlaceholder', {
        min: minAmount,
        currency: currentCurrencyCode,
      })
    : t('common.amountPlaceholder');

  const transformedCommissionData =
    isCommissionData &&
    commissionData &&
    typeof commissionData === 'object' &&
    'feeAmount' in commissionData
      ? {
          feeAmount: (commissionData as { feeAmount: number }).feeAmount,
          currencyCode: watchedValues.currencyCode,
          receiveAmount:
            Number(normalizedAmount) -
            (commissionData as { feeAmount: number }).feeAmount,
        }
      : undefined;

  return {
    form: bankTransferForm,
    watchedValues,
    handleFormSubmit,

    // Data
    currencyOptions,
    minAmount,
    amountPlaceholder,
    filteredCountries,
    bankOptions,

    // Commission
    commissionData: transformedCommissionData,
    isCommissionFetching,
    commissionError,
    shouldShowCommission,
    isCommissionPending,

    // States
    isSubmitInProgressWithoutErrors,
    formError: commissionError || createOperationDepositError,

    // Loading states
    isLoading: isCountriesLoading,
    isAccountInfoError: false,
    accountsInfoError: null,

    // Error states
    hasDataError: false,
    firstError: countriesError,
  };
};
